import BaseFormController from "../../validation/BaseFormController";
import {FormField, OptionalFormField, RequiredFormField} from "../../validation/FormField";
import {none, Option, option} from "ts-option";
import FormValidation from "../../validation/FormValidation";
import {ValidationFailure, ValidationResult} from "../../validation/ValidationResult";
import FormValidators from "../../validation/FormValidators";
import Address from "../../model/address/Address";
import PartnerBrand, {PartnerBrandCurrency} from "../../model/partnerBrand/PartnerBrand";
import {CreditValueCapMode} from "../../model/partnerBrand/CreditValueCapMode";
import NumberUtils from "../../utils/NumberUtils";
import {
	DealerPriceMatrixState,
	createEmptyMatrixState,
	fromDealerPrices,
	hasAtLeastOneFilledCell,
	isMatrixValid,
	toDealerPrices
} from "../brand/dto/DealerPriceMatrixState";

const uuidv4 = require("uuid/v4");

/**
 * Brand form data.
 */
export default class PartnerBrandForm extends BaseFormController<PartnerBrandForm> {
	constructor(formId: string,
		public readonly brandId: RequiredFormField<string>,
		public readonly name: RequiredFormField<string>,
		public readonly tradingName: OptionalFormField<string>,
		public readonly labourCost: RequiredFormField<string>,
		public readonly markup: RequiredFormField<number>,
		public readonly domain: OptionalFormField<string>,
		public readonly phoneNumber: RequiredFormField<string>,
		public readonly logoId: OptionalFormField<string>,
		public readonly address: RequiredFormField<Address>,
		public readonly vatRegistrationNumber: OptionalFormField<string>,
		public readonly freeText: OptionalFormField<string>,
		public readonly gasNumber: OptionalFormField<string>,
		public readonly email: OptionalFormField<string>,
		public readonly quoteEmailStyleSuffix: RequiredFormField<string>,
		public readonly vatRegistered: RequiredFormField<boolean>,
		public readonly bdpId: RequiredFormField<string>,
		public readonly encourageSms: OptionalFormField<boolean>,
		public readonly accountManagerId: OptionalFormField<string>,
		public readonly noteImportant: OptionalFormField<string>,
		public readonly billDueDays: RequiredFormField<number>,
		public readonly invoiceDueDays: RequiredFormField<number>,
		public readonly remittanceNotice: RequiredFormField<boolean>,
		public readonly preferredMerchantId: OptionalFormField<string>,
		public readonly leadUserId: OptionalFormField<string>,
		public readonly zendeskSellCompanyCode: OptionalFormField<string>,
		public readonly priceAdjustmentFactor: RequiredFormField<number>,
		public readonly preferredMerchantBranchLocationId: OptionalFormField<string>,
		public readonly automaticPayment: RequiredFormField<boolean>,
		public readonly showRemittanceOnMobile: RequiredFormField<boolean>,
		public readonly gasCertAvailable: RequiredFormField<boolean>,
		public readonly serviceReminderAvailable: RequiredFormField<boolean>,
		public readonly defaultUtilityId: RequiredFormField<string>,
		public readonly currency: RequiredFormField<PartnerBrandCurrency>,
		public readonly creditValueAirToWaterHp: OptionalFormField<string>,
		public readonly creditValueAirToAirHp: OptionalFormField<string>,
		public readonly creditValueWaterToWaterHp: OptionalFormField<string>,
		public readonly creditValueBrineToWaterHp: OptionalFormField<string>,
		public readonly creditValueFlowsave: OptionalFormField<string>,
		public readonly creditValueCapMode: RequiredFormField<CreditValueCapMode>,
		public readonly creditValueCap: OptionalFormField<string>,
		public readonly dealerPriceMatrix: DealerPriceMatrixState,
		public readonly validated: boolean = false) {
		super(name, formId);
	}

	static createBrandForm({
		brandId = none,
		name = none,
		tradingName = none,
		labourCost = none,
		markup = none,
		domain = none,
		phoneNumber = none,
		logoId = none,
		address = none,
		vatRegistrationNumber = none,
		freeText = none,
		gasNumber = none,
		email = none,
		quoteEmailStyleSuffix = none,
		vatRegistered = none,
		bdpId = none,
		encourageSms = none,
		accountManagerId = none,
		noteImportant = none,
		billDueDays = none,
		invoiceDueDays = none,
		remittanceNotice = none,
		preferredMerchantId = none,
		leadUserId = none,
		zendeskSellCompanyCode = none,
		priceAdjustmentFactor = none,
		preferredMerchantBranchLocationId = none,
		automaticPayment = none,
		showRemittanceOnMobile = none,
		gasCertAvailable = none,
		serviceReminderAvailable = none,
		defaultUtilityId = none,
		currency = none,
		creditValueAirToWaterHp = none,
		creditValueAirToAirHp = none,
		creditValueWaterToWaterHp = none,
		creditValueBrineToWaterHp = none,
		creditValueFlowsave = none,
		creditValueCapMode = none,
		creditValueCap = none,
		dealerPriceMatrix = createEmptyMatrixState()
	}) {
		return new PartnerBrandForm(
			uuidv4(),
			RequiredFormField.create<string>(brandId, "Parent brand is required", [
				FormValidators.nonEmptyString("Parent brand is required")
			]),
			RequiredFormField.create<string>(name, "Partner brand name is required", [
				FormValidators.nonEmptyString("Partner brand name is required")
			]),
			OptionalFormField.create<string>(tradingName),
			RequiredFormField.create<string>(labourCost, "Fixed reward rate is required",
				[FormValidators.notLessThanValue("Fixed reward rate should be at least 1.00", 1)]),
			RequiredFormField.create<number>(markup, "Markup is required"),
			OptionalFormField.create<string>(domain, [FormValidators.optionalWebsite("Wrong format. Fe. https://hiber.com")]),
			RequiredFormField.create<string>(phoneNumber, "Phone number is required",
				[FormValidators.isPhoneNumberFormat("Invalid phone number")]),
			OptionalFormField.create<string>(logoId),
			RequiredFormField.create<Address>(address, "Address is required"),
			OptionalFormField.create<string>(vatRegistrationNumber),
			OptionalFormField.create<string>(freeText),
			OptionalFormField.create<string>(gasNumber, [
				FormValidators.maxLength("Gas safe number must be maximum 6 digits", 6),
				FormValidators.digitsOnly("Gas safe number must contain digits only")
			]),
			OptionalFormField.create<string>(email, [FormValidators.optionalEmail]),
			RequiredFormField.create<string>(quoteEmailStyleSuffix, "Quote e-mail template theme is required"),
			RequiredFormField.create<boolean>(vatRegistered, "VAT registered status is required"),
			RequiredFormField.create<string>(bdpId, "Business Development Partner is required",
				[FormValidators.nonEmptyString("Business Development Partner is required")]),
			OptionalFormField.create<boolean>(encourageSms),
			OptionalFormField.create<string>(accountManagerId),
			OptionalFormField.create<string>(noteImportant),
			RequiredFormField.create<number>(billDueDays, "Bill due days number is required"),
			RequiredFormField.create<number>(invoiceDueDays, "Customer invoice due days number is required"),
			RequiredFormField.create<boolean>(remittanceNotice),
			OptionalFormField.create<string>(preferredMerchantId),
			OptionalFormField.create<string>(leadUserId),
			OptionalFormField.create<string>(zendeskSellCompanyCode),
			RequiredFormField.create<number>(priceAdjustmentFactor, "Price Adjustment Factor is required",
				[FormValidators.notMoreThanValue("Price Adjustment Factor should be at most 999", 999)]),
			OptionalFormField.create<string>(preferredMerchantBranchLocationId),
			RequiredFormField.create<boolean>(automaticPayment),
			RequiredFormField.create<boolean>(showRemittanceOnMobile),
			RequiredFormField.create<boolean>(gasCertAvailable),
			RequiredFormField.create<boolean>(serviceReminderAvailable),
			RequiredFormField.create<string>(defaultUtilityId, "Default utility is required"),
			RequiredFormField.create<PartnerBrandCurrency>(currency, "Currency is required"),
			OptionalFormField.create<string>(creditValueAirToWaterHp),
			OptionalFormField.create<string>(creditValueAirToAirHp),
			OptionalFormField.create<string>(creditValueWaterToWaterHp),
			OptionalFormField.create<string>(creditValueBrineToWaterHp),
			OptionalFormField.create<string>(creditValueFlowsave),
			RequiredFormField.create<CreditValueCapMode>(creditValueCapMode, "Credit value cap is required"),
			OptionalFormField.create<string>(creditValueCap),
			dealerPriceMatrix
		)
	}

	public static createEmpty(quoteEmailStyleSuffix: Option<string>): PartnerBrandForm {
		return this.createBrandForm(
			{
				encourageSms: option(true),
				billDueDays: option(5),
				invoiceDueDays: option(5),
				markup: option(1),
				labourCost: option("1.00"),
				quoteEmailStyleSuffix,
				remittanceNotice: option(true),
				priceAdjustmentFactor: (option(0)),
				automaticPayment: option(true),
				vatRegistered: option(false),
				showRemittanceOnMobile: option(true),
				gasCertAvailable: option(false),
				serviceReminderAvailable: option(true),
				currency: option(PartnerBrandCurrency.EUR),
				// A new partner takes the brand's cap. Nobody registers a taxi driver from this screen -
				// their journey sets "no cap" as the account is created.
				creditValueCapMode: option(CreditValueCapMode.INHERIT)
			});
	}

	static createFromPartnerBrand(partnerBrand: PartnerBrand, address: Option<Address>) {
		return PartnerBrandForm.createBrandForm({
			brandId: option(partnerBrand.brandId),
			name: option(partnerBrand.name),
			tradingName: partnerBrand.tradingName,
			labourCost: option((partnerBrand.labourCost / 100).toFixed(2)),
			markup: option(partnerBrand.markup),
			domain: partnerBrand.domain,
			phoneNumber: option(partnerBrand.phoneNumber),
			logoId: partnerBrand.logoId,
			address: address,
			vatRegistrationNumber: partnerBrand.vatRegistrationNumber,
			freeText: partnerBrand.freeText,
			gasNumber: partnerBrand.gasNumber,
			email: partnerBrand.email,
			quoteEmailStyleSuffix: option(partnerBrand.quoteEmailStyleSuffix),
			vatRegistered: option(partnerBrand.vatRegistered),
			bdpId: partnerBrand.bdpId,
			encourageSms: option(partnerBrand.encourageSms),
			accountManagerId: partnerBrand.accountManagerId,
			noteImportant: partnerBrand.noteImportant,
			billDueDays: option(partnerBrand.billDueDays),
			invoiceDueDays: option(partnerBrand.invoiceDueDays),
			remittanceNotice: option(partnerBrand.remittanceNotice).orElse(() => option(false)),
			preferredMerchantId: partnerBrand.preferredMerchantId,
			leadUserId: partnerBrand.leadUserId,
			zendeskSellCompanyCode: partnerBrand.zendeskSellCompanyCode,
			priceAdjustmentFactor: option(partnerBrand.priceAdjustmentFactor),
			preferredMerchantBranchLocationId: partnerBrand.preferredMerchantBranchLocationId,
			automaticPayment: option(partnerBrand.automaticPayment),
			showRemittanceOnMobile: option(partnerBrand.showRemittanceOnMobile),
			gasCertAvailable: option(partnerBrand.gasCertAvailable),
			serviceReminderAvailable: option(partnerBrand.serviceReminderAvailable),
			defaultUtilityId: option(partnerBrand.defaultUtilityId),
			currency: option(partnerBrand.currency),
			dealerPriceMatrix: fromDealerPrices(partnerBrand.dealerPrices),
			creditValueAirToWaterHp: partnerBrand.creditValueAirToWaterHp.map(v => (v / 100).toFixed(2)),
			creditValueAirToAirHp: partnerBrand.creditValueAirToAirHp.map(v => (v / 100).toFixed(2)),
			creditValueWaterToWaterHp: partnerBrand.creditValueWaterToWaterHp.map(v => (v / 100).toFixed(2)),
			creditValueBrineToWaterHp: partnerBrand.creditValueBrineToWaterHp.map(v => (v / 100).toFixed(2)),
			creditValueFlowsave: partnerBrand.creditValueFlowsave.map(v => (v / 100).toFixed(2)),
			creditValueCapMode: option(partnerBrand.creditValueCapMode),
			creditValueCap: partnerBrand.creditValueCap.map(v => (v / 100).toFixed(2))
		});
	}

	validate(): PartnerBrandForm {
		const validated = FormValidation.validateForm<PartnerBrandForm>(this, (form, config) => form.copy(config));
		return validated.copy({validated: true});
	}

	getDealerPriceMatrixError(): string | undefined {
		if (!this.validated) { return undefined; }
		if (hasAtLeastOneFilledCell(this.dealerPriceMatrix) && !isMatrixValid(this.dealerPriceMatrix)) {
			return "Some dealer prices are invalid";
		}
		return undefined;
	}

	/**
	 * Explains a fixed cap with no amount behind it, which is the one way these two fields can disagree.
	 * Left to a cross-field check rather than a validator, because the amount is genuinely optional for
	 * the other two modes - the same shape as the dealer price matrix errors.
	 */
	getCreditValueCapError(): string | undefined {
		if (!this.validated || !this.isFixedCapWithoutAmount()) { return undefined; }
		return "An amount is required when you cap at a different amount";
	}

	onValid(onValid: (partnerBrand: PartnerBrand) => void): ValidationResult<PartnerBrand> {
		if (hasAtLeastOneFilledCell(this.dealerPriceMatrix) && !isMatrixValid(this.dealerPriceMatrix)) {
			return new ValidationFailure<PartnerBrand>([]);
		}
		// Refused here rather than sent: the backend rejects it too, and a partner saved with a fixed cap
		// and no amount would quietly go on inheriting while the screen said otherwise.
		if (this.isFixedCapWithoutAmount()) {
			return new ValidationFailure<PartnerBrand>([]);
		}
		return FormValidation.validateFields(
			[this.brandId, this.name, this.address, this.labourCost, this.markup, this.phoneNumber, this.email, this.quoteEmailStyleSuffix,
				this.domain, this.vatRegistered, this.bdpId, this.billDueDays, this.invoiceDueDays, this.gasNumber,
				this.remittanceNotice, this.priceAdjustmentFactor, this.automaticPayment, this.showRemittanceOnMobile,
				this.gasCertAvailable, this.serviceReminderAvailable, this.defaultUtilityId, this.currency,
				this.creditValueCapMode, this.creditValueCap],
			() => {
				return new PartnerBrand(
					uuidv4,
					this.brandId.getValue().trim(),
					this.name.getValue().trim(),
					this.tradingName.getOptionalValue(),
					NumberUtils.multiplyAndRound(Number(this.labourCost.getValue()), 100),
					this.markup.getValue(),
					this.domain.getOptionalValue(),
					this.phoneNumber.getValue(),
					this.logoId.getOptionalValue(),
					none,
					this.vatRegistrationNumber.getOptionalValue(),
					this.freeText.getOptionalValue(),
					this.gasNumber.getOptionalValue(),
					this.email.getOptionalValue(),
					this.quoteEmailStyleSuffix.getValue(),
					this.vatRegistered.getValue(),
					this.bdpId.getOptionalValue(),
					this.encourageSms.getValue().getOrElse(false),
					this.accountManagerId.getOptionalValue(),
					this.noteImportant.getOptionalValue(),
					none,
					none,
					this.billDueDays.getValue(),
					this.invoiceDueDays.getValue(),
					this.remittanceNotice.getValue(),
					this.preferredMerchantId.getOptionalValue(),
					this.leadUserId.getOptionalValue(),
					this.zendeskSellCompanyCode.getOptionalValue(),
					this.priceAdjustmentFactor.getValue(),
					this.preferredMerchantBranchLocationId.getOptionalValue(),
					this.automaticPayment.getValue(),
					none,
					none,
					none,
					this.showRemittanceOnMobile.getValue(),
					this.gasCertAvailable.getValue(),
					this.serviceReminderAvailable.getValue(),
					this.defaultUtilityId.getValue(),
					toDealerPrices(this.dealerPriceMatrix),
					this.creditValueAirToWaterHp.getOptionalValue().map(v => NumberUtils.multiplyAndRound(Number(v), 100)),
					this.creditValueAirToAirHp.getOptionalValue().map(v => NumberUtils.multiplyAndRound(Number(v), 100)),
					this.creditValueWaterToWaterHp.getOptionalValue().map(v => NumberUtils.multiplyAndRound(Number(v), 100)),
					this.creditValueBrineToWaterHp.getOptionalValue().map(v => NumberUtils.multiplyAndRound(Number(v), 100)),
					this.creditValueFlowsave.getOptionalValue().map(v => NumberUtils.multiplyAndRound(Number(v), 100)),
					this.currency.getValue(),
					this.creditValueCapMode.getValue(),
					this.creditValueCap.getOptionalValue().map(v => NumberUtils.multiplyAndRound(Number(v), 100))
				);
			}).onSuccess(brand => onValid(brand));
	}

	copy({
		brandId = this.brandId,
		name = this.name,
		tradingName = this.tradingName,
		labourCost = this.labourCost,
		markup = this.markup,
		domain = this.domain,
		phoneNumber = this.phoneNumber,
		logoId = this.logoId,
		address = this.address,
		vatRegistrationNumber = this.vatRegistrationNumber,
		freeText = this.freeText,
		gasNumber = this.gasNumber,
		email = this.email,
		quoteEmailStyleSuffix = this.quoteEmailStyleSuffix,
		vatRegistered = this.vatRegistered,
		bdpId = this.bdpId,
		encourageSms = this.encourageSms,
		accountManagerId = this.accountManagerId,
		noteImportant = this.noteImportant,
		billDueDays = this.billDueDays,
		invoiceDueDays = this.invoiceDueDays,
		remittanceNotice = this.remittanceNotice,
		preferredMerchantId = this.preferredMerchantId,
		leadUserId = this.leadUserId,
		zendeskSellCompanyCode = this.zendeskSellCompanyCode,
		priceAdjustmentFactor = this.priceAdjustmentFactor,
		preferredMerchantBranchLocationId = this.preferredMerchantBranchLocationId,
		automaticPayment = this.automaticPayment,
		showRemittanceOnMobile = this.showRemittanceOnMobile,
		gasCertAvailable = this.gasCertAvailable,
		serviceReminderAvailable = this.serviceReminderAvailable,
		defaultUtilityId = this.defaultUtilityId,
		currency = this.currency,
		dealerPriceMatrix = this.dealerPriceMatrix,
		creditValueAirToWaterHp = this.creditValueAirToWaterHp,
		creditValueAirToAirHp = this.creditValueAirToAirHp,
		creditValueWaterToWaterHp = this.creditValueWaterToWaterHp,
		creditValueBrineToWaterHp = this.creditValueBrineToWaterHp,
		creditValueFlowsave = this.creditValueFlowsave,
		creditValueCapMode = this.creditValueCapMode,
		creditValueCap = this.creditValueCap,
		validated = this.validated
	}): PartnerBrandForm {
		return new PartnerBrandForm(this.formId, brandId, name, tradingName, labourCost, markup, domain, phoneNumber, logoId,
			address, vatRegistrationNumber, freeText, gasNumber, email, quoteEmailStyleSuffix, vatRegistered, bdpId, encourageSms,
			accountManagerId, noteImportant, billDueDays, invoiceDueDays, remittanceNotice, preferredMerchantId, leadUserId,
			zendeskSellCompanyCode, priceAdjustmentFactor, preferredMerchantBranchLocationId, automaticPayment,
			showRemittanceOnMobile, gasCertAvailable, serviceReminderAvailable, defaultUtilityId, currency,
			creditValueAirToWaterHp, creditValueAirToAirHp, creditValueWaterToWaterHp, creditValueBrineToWaterHp,
			creditValueFlowsave, creditValueCapMode, creditValueCap, dealerPriceMatrix, validated);
	}

	fields(): Array<FormField<any>> {
		return [this.name, this.email, this.domain];
	}

	private isFixedCapWithoutAmount(): boolean {
		return this.creditValueCapMode.getOptionalValue().exists(mode => mode === CreditValueCapMode.FIXED)
			&& this.creditValueCap.getOptionalValue().isEmpty;
	}
}
